import { useEffect, useMemo, useState } from "react";
import createFileEditorHandlers from "../hooks/devIdeFileEditorHandlers";
import createSandboxHandlers from "../hooks/devIdeSandboxHandlers";
import {
  buildAgentHandoffSummary,
  buildZipImplementationPrompt,
} from "../utils/devIdePromptGenerators";


const WORKFLOW_STATUS_STEPS = [
  ["packageExported", "Package exported"],
  ["packageImported", "Package imported"],
  ["changedFilesSaved", "Changed files saved"],
  ["frontendBuildCompleted", "Frontend build completed"],
  ["backendRestartCompleted", "Backend restart completed"],
  ["verificationSummaryCopied", "Verification summary copied"],
];

const createInitialWorkflowStatus = () =>
  WORKFLOW_STATUS_STEPS.reduce((acc, [key, label]) => {
    acc[key] = {
      label,
      state: "pending",
      timestamp: "",
      message: "",
    };
    return acc;
  }, {});

const mergeWorkflowStatus = (incomingStatus) => {
  const base = createInitialWorkflowStatus();
  Object.entries(incomingStatus || {}).forEach(([key, value]) => {
    if (!base[key]) return;
    base[key] = {
      ...base[key],
      ...(value || {}),
      label: (value && value.label) || base[key].label,
      state: (value && value.state) || base[key].state,
      timestamp: (value && value.timestamp) || "",
      message: (value && value.message) || "",
    };
  });
  return base;
};

const useDevIdePanelState = (currentUsername, options = {}) => {
  // NEW: projects selector state
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [localSelectedProjectId, setLocalSelectedProjectId] = useState("");
  const selectedProjectId =
    typeof options.selectedProjectId === "string"
      ? options.selectedProjectId
      : localSelectedProjectId;
  const setSelectedProjectId =
    typeof options.setSelectedProjectId === "function"
      ? options.setSelectedProjectId
      : setLocalSelectedProjectId;
  const importContext = options.importContext || null;
  const onImportContextHandled =
    typeof options.onImportContextHandled === "function"
      ? options.onImportContextHandled
      : null;

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState("ALL");

  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileMeta, setSelectedFileMeta] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [fileOriginalContent, setFileOriginalContent] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const [gptPasteContent, setGptPasteContent] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // NEW: global error popup state (used by save-file + rebuild frontend)
  const [saveErrorMessage, setSaveErrorMessage] = useState(null);
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);

  const [filterText, setFilterText] = useState("");

  // diff preview state
  const [diffText, setDiffText] = useState("");
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);
  const [diffLineCount, setDiffLineCount] = useState(null);

  // sandbox action buttons
  const [backendActionLoading, setBackendActionLoading] = useState(false);
  const [frontendActionLoading, setFrontendActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState(createInitialWorkflowStatus);
  const [workflowStatusLoading, setWorkflowStatusLoading] = useState(false);
  const [workflowStatusError, setWorkflowStatusError] = useState(null);

  const getWorkflowChangeId = () =>
    activeChange && activeChange.id ? activeChange.id : 0;

  const loadWorkflowStatus = async (projectIdOverride, changeIdOverride) => {
    const projectId = projectIdOverride || selectedProjectId || "default";
    const changeId =
      typeof changeIdOverride === "number" || typeof changeIdOverride === "string"
        ? changeIdOverride
        : getWorkflowChangeId();

    setWorkflowStatusLoading(true);
    setWorkflowStatusError(null);

    try {
      const params = new URLSearchParams();
      params.set("project_id", projectId);
      params.set("change_id", changeId || 0);
      const res = await fetch(`/api/dev-ide/workflow-status?${params.toString()}`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load workflow status.");
      }
      setWorkflowStatus(mergeWorkflowStatus(data.workflow_status));
    } catch (err) {
      setWorkflowStatusError(err.message);
    } finally {
      setWorkflowStatusLoading(false);
    }
  };

  const markWorkflowStep = (stepKey, state = "success", message = "") => {
    const stepExists = WORKFLOW_STATUS_STEPS.some(([key]) => key === stepKey);
    if (!stepExists) return;

    const label =
      (WORKFLOW_STATUS_STEPS.find(([key]) => key === stepKey) || [null, stepKey])[1];
    const timestamp = new Date().toLocaleString();

    setWorkflowStatus((prev) => ({
      ...(prev || createInitialWorkflowStatus()),
      [stepKey]: {
        ...((prev || {})[stepKey] || {}),
        label: ((prev || {})[stepKey] || {}).label || label,
        state,
        timestamp,
        message: message || "",
      },
    }));

    const projectId = selectedProjectId || "default";
    const changeId = getWorkflowChangeId();
    fetch("/api/dev-ide/workflow-status/step", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_id: projectId,
        change_id: changeId,
        step_key: stepKey,
        state,
        message: message || "",
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success) {
          setWorkflowStatus(mergeWorkflowStatus(data.workflow_status));
          setWorkflowStatusError(null);
        } else if (data && data.error) {
          setWorkflowStatusError(data.error);
        }
      })
      .catch((err) => {
        setWorkflowStatusError(err.message);
      });
  };

  const resetWorkflowStatus = () => {
    const blankStatus = createInitialWorkflowStatus();
    setWorkflowStatus(blankStatus);
    setWorkflowStatusError(null);

    fetch("/api/dev-ide/workflow-status/reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_id: selectedProjectId || "default",
        change_id: getWorkflowChangeId(),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success) {
          setWorkflowStatus(mergeWorkflowStatus(data.workflow_status));
        } else if (data && data.error) {
          setWorkflowStatusError(data.error);
        }
      })
      .catch((err) => {
        setWorkflowStatusError(err.message);
      });
  };

  // change management state
  const [changes, setChanges] = useState([]);
  const [activeChange, setActiveChange] = useState(null);
  const [changeFiles, setChangeFiles] = useState([]);
  const [changeFilterMode, setChangeFilterMode] = useState("ALL");
  const [changeError, setChangeError] = useState(null);
  const [revertWholeChangeLoading, setRevertWholeChangeLoading] = useState(false);

  // file history state
  const [fileHistory, setFileHistory] = useState([]);
  const [fileHistoryLoading, setFileHistoryLoading] = useState(false);
  const [fileHistoryError, setFileHistoryError] = useState(null);

  // create module state
  const [newModuleOpen, setNewModuleOpen] = useState(false);
  const [newModuleName, setNewModuleName] = useState("");
  const [newModuleParent, setNewModuleParent] = useState(""); // <-- NEW
  const [newModuleBackend, setNewModuleBackend] = useState(true);
  const [newModuleFrontend, setNewModuleFrontend] = useState(true);
  const [newModuleError, setNewModuleError] = useState(null);
  const [newModuleLoading, setNewModuleLoading] = useState(false);


  // create app state
  const [newAppOpen, setNewAppOpen] = useState(false);
  const [newAppForm, setNewAppForm] = useState({
    app_name: "New App",
    project_id: "new_app_sandbox",
    base_path: "/opt/apps/new_app_sandbox",
    backend_root_path: "/opt/apps/new_app_sandbox",
    frontend_root_path: "/opt/apps/new_app_sandbox/frontend",
    backend_port: 5050,
    starter_module: "home",
    server_name: "_",
    overwrite_existing: false,
    enable_systemd: true,
    enable_nginx: true,
    auto_build_frontend: true,
    open_firewall: true,
  });
  const [newAppPreview, setNewAppPreview] = useState(null);
  const [newAppError, setNewAppError] = useState(null);
  const [newAppPreviewLoading, setNewAppPreviewLoading] = useState(false);
  const [newAppCreateLoading, setNewAppCreateLoading] = useState(false);

  // create file state
  const [newFileOpen, setNewFileOpen] = useState(false);
  const [newFileModule, setNewFileModule] = useState("");
  const [newFileSide, setNewFileSide] = useState("backend");
  const [newFileFolder, setNewFileFolder] = useState("routes");
  const [newFileName, setNewFileName] = useState("");
  const [newFileType, setNewFileType] = useState("");
  const [newFileError, setNewFileError] = useState(null);
  const [newFileLoading, setNewFileLoading] = useState(false);

  // ZIP import dialog state
  const [importZipOpen, setImportZipOpen] = useState(false);
  const [importZipIncludeDocs, setImportZipIncludeDocs] = useState(false);
  const [importZipInitialPath, setImportZipInitialPath] = useState("");
  const [importZipAutoImportFromPath, setImportZipAutoImportFromPath] = useState(false);
  const [importZipCodexHandoffContext, setImportZipCodexHandoffContext] = useState(null);
  const [importZipImportContext, setImportZipImportContext] = useState(null);
  const [downloadZipDialogOpen, setDownloadZipDialogOpen] = useState(false);

  // SQL migration state (CHG-047)
  const [sqlFiles, setSqlFiles] = useState([]);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlError, setSqlError] = useState(null);
  const [newSqlOpen, setNewSqlOpen] = useState(false);
  const [newSqlSequence, setNewSqlSequence] = useState("");
  const [newSqlDescription, setNewSqlDescription] = useState("");
  const [newSqlError, setNewSqlError] = useState(null);
  const [newSqlLoading, setNewSqlLoading] = useState(false);

  // SQL editor state
  const [sqlEditorOpen, setSqlEditorOpen] = useState(false);
  const [selectedSqlFile, setSelectedSqlFile] = useState(null);
  const [sqlEditorContent, setSqlEditorContent] = useState("");
  const [sqlEditorOriginalContent, setSqlEditorOriginalContent] =
    useState("");
  const [sqlEditorLoading, setSqlEditorLoading] = useState(false);
  const [sqlEditorError, setSqlEditorError] = useState(null);
  const [sqlSaveLoading, setSqlSaveLoading] = useState(false);
  const [sqlSaveMessage, setSqlSaveMessage] = useState(null);

  // rules panel state (CHG-037)
  const [rulesMarkdown, setRulesMarkdown] = useState("");
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState(null);

  // ---------------------------------------------------------------------------
  // LOADERS
  // ---------------------------------------------------------------------------

  // NEW: Load projects
  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError(null);

    try {
      const res = await fetch("/api/dev-ide/projects");
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load projects.");
      }

      const list = data.projects || [];
      setProjects(list);

      // Default selection if not set
      if (!selectedProjectId && list.length > 0) {
        const first = list[0];
        if (first && first.project_id) {
          setSelectedProjectId(first.project_id);
          return first.project_id;
        }
      }

      return selectedProjectId || "";
    } catch (err) {
      setProjectsError(err.message);
      return selectedProjectId || "";
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadChangeFiles = async (changeId) => {
    try {
      const res = await fetch(
        `/api/dev-ide/change-files?change_id=${encodeURIComponent(changeId)}${
          selectedProjectId
            ? `&project_id=${encodeURIComponent(selectedProjectId)}`
            : ""
        }`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load change files.");
      }
      setChangeFiles(data.files || []);
    } catch (err) {
      setChangeError(err.message);
    }
  };

  const loadSqlFiles = async (changeId) => {
    if (!changeId) {
      setSqlFiles([]);
      setSqlError(null);
      return;
    }

    setSqlLoading(true);
    setSqlError(null);

    try {
      const res = await fetch(
        `/api/dev-ide/sql/list?change_id=${encodeURIComponent(changeId)}${
          selectedProjectId
            ? `&project_id=${encodeURIComponent(selectedProjectId)}`
            : ""
        }`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load SQL files.");
      }
      setSqlFiles(data.sql_files || []);
    } catch (err) {
      setSqlError(err.message);
    } finally {
      setSqlLoading(false);
    }
  };

  const refreshFilesList = async () => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      const params = new URLSearchParams();
      if (selectedProjectId) {
        params.set("project_id", selectedProjectId);
      }

      const res = await fetch(
        `/api/dev-ide/files${params.toString() ? `?${params.toString()}` : ""}`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load files.");
      }
      const fileList = data.files || [];
      setFiles(fileList);

      const distinctModules = Array.from(
        new Set(
          fileList
            .map((f) => f.module)
            .filter((m) => m && String(m).trim().length > 0)
        )
      ).sort((a, b) => a.localeCompare(b));
      setModules(distinctModules);
      return fileList;
    } catch (err) {
      setFilesError(err.message);
      return [];
    } finally {
      setFilesLoading(false);
    }
  };

  const setActiveChangeById = async (changeId, projectIdOverride) => {
    if (!changeId) {
      setActiveChange(null);
      setChangeFiles([]);
      setSqlFiles([]);
      setSqlError(null);
      return;
    }

    try {
      const res = await fetch("/api/dev-ide/active-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          change_id: changeId,
          project_id: projectIdOverride || selectedProjectId || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to set active change.");
      }
      const ac = data.active_change || null;
      setActiveChange(ac);
      if (ac && ac.id) {
        await loadChangeFiles(ac.id);
        await loadSqlFiles(ac.id);
      } else {
        setChangeFiles([]);
        setSqlFiles([]);
        setSqlError(null);
      }
    } catch (err) {
      setChangeError(err.message);
    }
  };

  useEffect(() => {
    const init = async () => {
      const projId = await loadProjects();

      const loadFiles = async (effectiveProjectId) => {
        setFilesLoading(true);
        setFilesError(null);
        try {
          const params = new URLSearchParams();
          if (effectiveProjectId) {
            params.set("project_id", effectiveProjectId);
          }

          const res = await fetch(
            `/api/dev-ide/files${
              params.toString() ? `?${params.toString()}` : ""
            }`
          );
          const data = await res.json();
          if (!data.success) {
            throw new Error(data.error || "Failed to load files.");
          }
          const fileList = data.files || [];
          setFiles(fileList);

          const distinctModules = Array.from(
            new Set(
              fileList
                .map((f) => f.module)
                .filter((m) => m && String(m).trim().length > 0)
            )
          ).sort((a, b) => a.localeCompare(b));
          setModules(distinctModules);
        } catch (err) {
          setFilesError(err.message);
        } finally {
          setFilesLoading(false);
        }
      };

      const loadChanges = async (effectiveProjectId) => {
        try {
          const res = await fetch(
            `/api/dev-ide/changes${
              effectiveProjectId
                ? `?project_id=${encodeURIComponent(effectiveProjectId)}`
                : ""
            }`
          );
          const data = await res.json();
          if (!data.success) {
            throw new Error(data.error || "Failed to load changes.");
          }
          setChanges(data.changes || []);
        } catch (err) {
          setChangeError(err.message);
        }
      };

      const loadActiveChange = async (effectiveProjectId) => {
        try {
          const res = await fetch(
            `/api/dev-ide/active-change${
              effectiveProjectId
                ? `?project_id=${encodeURIComponent(effectiveProjectId)}`
                : ""
            }`
          );
          const data = await res.json();
          if (!data.success) {
            return;
          }
          const ac = data.active_change || null;
          setActiveChange(ac);
          if (ac && ac.id) {
            await loadChangeFiles(ac.id);
            await loadSqlFiles(ac.id);
          } else {
            setChangeFiles([]);
            setSqlFiles([]);
          }
        } catch {
          // swallow errors so IDE still works if backend not ready
        }
      };

      await loadFiles(projId);
      await loadChanges(projId);
      await loadActiveChange(projId);
    };

    init();
  }, []);

  // NEW: When project changes, clear editor selection and reload file list
  useEffect(() => {
    if (!selectedProjectId) return;

    setSelectedFileId(null);
    setSelectedFileMeta(null);
    setFileContent("");
    setFileOriginalContent("");
    setFileError(null);

    setDiffText("");
    setDiffError(null);
    setDiffLineCount(null);

    setSaveMessage(null);
    setGptPasteContent("");
    setChangeSummary("");

    const syncProjectContext = async () => {
      await refreshFilesList();
      if (activeChange && activeChange.id) {
        await loadChangeFiles(activeChange.id);
        await loadSqlFiles(activeChange.id);
      }
    };

    syncProjectContext();
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    loadWorkflowStatus(selectedProjectId, getWorkflowChangeId());
  }, [selectedProjectId, activeChange && activeChange.id]);

  // Load rules whenever selectedModule changes (CHG-037)
  useEffect(() => {
    const moduleName =
      selectedModule && selectedModule.trim().length > 0
        ? selectedModule
        : "ALL";

    const loadRules = async () => {
      setRulesLoading(true);
      setRulesError(null);

      try {
        const params = new URLSearchParams();
        params.set("module", encodeURIComponent(moduleName));
        if (selectedProjectId) {
          params.set("project_id", selectedProjectId);
        }

        // NOTE: module is already encoded above; avoid double-encoding in string building
        const res = await fetch(
          `/api/dev-ide/rules?module=${encodeURIComponent(moduleName)}${
            selectedProjectId
              ? `&project_id=${encodeURIComponent(selectedProjectId)}`
              : ""
          }`
        );
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || "Failed to load rules.");
        }
        setRulesMarkdown(data.rules_markdown || "");
      } catch (err) {
        setRulesMarkdown("");
        setRulesError(err.message);
      } finally {
        setRulesLoading(false);
      }
    };

    loadRules();
  }, [selectedModule, selectedProjectId]);

  // ---------------------------------------------------------------------------
  // FILTERED FILES
  // ---------------------------------------------------------------------------

  const filteredFiles = useMemo(() => {
    if (!files || files.length === 0) return [];

    const lower = filterText.trim().toLowerCase();
    const pathsInChange = new Set(
      (changeFiles || []).map((cf) => cf.file_path)
    );

    return files.filter((f) => {
      if (selectedModule !== "ALL" && f.module !== selectedModule) {
        return false;
      }

      if (
        changeFilterMode === "ACTIVE_CHANGE_ONLY" &&
        activeChange &&
        activeChange.id &&
        !pathsInChange.has(f.path)
      ) {
        return false;
      }

      if (!lower) return true;

      return (
        (f.module && f.module.toLowerCase().includes(lower)) ||
        (f.path && f.path.toLowerCase().includes(lower)) ||
        (f.description && f.description.toLowerCase().includes(lower))
      );
    });
  }, [
    files,
    filterText,
    selectedModule,
    changeFilterMode,
    activeChange,
    changeFiles,
  ]);

  // ---------------------------------------------------------------------------
  // HANDLERS (CORE)
  // ---------------------------------------------------------------------------

  const handleSelectActiveChange = async (event) => {
    const value = event.target.value;
    if (!value) {
      await setActiveChangeById(null);
      return;
    }
    await setActiveChangeById(value);
  };

  const handleCreateNewChange = async () => {
    const title = window.prompt("Enter a title for the new change:");
    if (!title || !title.trim()) {
      return;
    }

    const effectiveModule =
      selectedModule && selectedModule !== "ALL" ? selectedModule : null;

    try {
      const res = await fetch("/api/dev-ide/changes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: "",
          module: effectiveModule,
          project_id: selectedProjectId || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to create change.");
      }

      const newChange = data.change;
      setChanges((prev) => [newChange, ...(prev || [])]);

      if (newChange && newChange.id) {
        await setActiveChangeById(newChange.id);
        setChangeFilterMode("ACTIVE_CHANGE_ONLY");
      }
    } catch (err) {
      setChangeError(err.message);
    }
  };

  const handleLoadFileHistory = async (mode = "ALL") => {
    if (!selectedFileMeta?.path) return;

    setFileHistoryLoading(true);
    setFileHistoryError(null);

    const params = new URLSearchParams();
    params.set("path", selectedFileMeta.path);
    if (mode === "ACTIVE_CHANGE_ONLY" && activeChange?.id) {
      params.set("change_id", activeChange.id);
    }
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }

    try {
      const res = await fetch(
        `/api/dev-ide/file-history?${params.toString()}`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load file history.");
      }
      setFileHistory(data.history || []);
    } catch (err) {
      setFileHistoryError(err.message);
    } finally {
      setFileHistoryLoading(false);
    }
  };

  const handleRevertFile = async (fileId, backupPath) => {
    if (!fileId || !backupPath) return;

    const backupName = String(backupPath).split('/').pop();
    const ok = window.confirm(`Revert file to this backup?

${backupName}`);
    if (!ok) return;

    try {
      const res = await fetch('/api/dev-ide/revert-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: fileId,
          backup_path: backupPath,
          username: currentUsername || 'dev-ide',
          change_id: activeChange && activeChange.id ? activeChange.id : null,
          project_id: selectedProjectId || null,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to revert file.');
      }

      setSaveMessage(data.message || 'Reverted file successfully.');

      // Reload file content and history
      await handleSelectFile(fileId);
      await handleLoadFileHistory('ALL');

      if (activeChange && activeChange.id) {
        await loadChangeFiles(activeChange.id);
        await loadSqlFiles(activeChange.id);
      }
    } catch (err) {
      setSaveMessage(`Error reverting file: ${err.message}`);
    }
  };



  const handleRevertWholeChange = async () => {
    if (!activeChange || !activeChange.id) return;

    const ok = window.confirm(
      `Revert all files for change #${activeChange.id} back to their pre-change state?\n\nThis will delete files created in the change and restore earlier file contents.`
    );
    if (!ok) return;

    setRevertWholeChangeLoading(true);
    setChangeError(null);

    try {
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(activeChange.id)}/revert`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: currentUsername || "dev-ide",
            project_id: selectedProjectId || null,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to revert change.");
      }

      const restoredCount = Number(data.restored_count || 0);
      const deletedCount = Number(data.deleted_count || 0);
      setSaveMessage(
        data.message ||
          `Reverted change. Restored ${restoredCount} file(s), deleted ${deletedCount} file(s).`
      );

      await refreshFilesList();
      await loadChangeFiles(activeChange.id);
      await loadSqlFiles(activeChange.id);

      if (selectedFileId) {
        try {
          await handleSelectFile(selectedFileId);
          await handleLoadFileHistory("ALL");
        } catch {
          setSelectedFileId(null);
          setSelectedFileMeta(null);
          setFileContent("");
          setFileOriginalContent("");
          setFileHistory([]);
        }
      }
    } catch (err) {
      setChangeError(err.message);
      setSaveMessage(`Error reverting change: ${err.message}`);
    } finally {
      setRevertWholeChangeLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // HANDLERS (DIALOGS + SQL)
  // ---------------------------------------------------------------------------


  useEffect(() => {
    const openQueuedImport = async () => {
      if (!importContext || !importContext.changeId) return;

      const projectId = importContext.projectId || selectedProjectId || "";
      if (projectId && projectId !== selectedProjectId) {
        setSelectedProjectId(projectId);
      }

      await setActiveChangeById(importContext.changeId, projectId);
      setImportZipIncludeDocs(importContext.includeDocuments !== false);
      setImportZipInitialPath(importContext.initialZipPath || "");
      setImportZipAutoImportFromPath(Boolean(importContext.autoImportFromPath));
      setImportZipCodexHandoffContext(importContext.codexHandoffContext || null);
      setImportZipImportContext(importContext);
      setImportZipOpen(true);
      if (onImportContextHandled) {
        onImportContextHandled();
      }
    };

    openQueuedImport();
  }, [importContext && importContext.requestedAt]);

  const handleOpenImportZip = () => {
    setImportZipIncludeDocs(false);
    setImportZipInitialPath("");
    setImportZipAutoImportFromPath(false);
    setImportZipCodexHandoffContext(null);
    setImportZipImportContext(null);
    setImportZipOpen(true);
  };

  const handleOpenImportZipWithDocs = () => {
    setImportZipIncludeDocs(true);
    setImportZipInitialPath("");
    setImportZipAutoImportFromPath(false);
    setImportZipCodexHandoffContext(null);
    setImportZipImportContext(null);
    setImportZipOpen(true);
  };

  const handleCloseImportZip = () => {
    setImportZipOpen(false);
    setImportZipInitialPath("");
    setImportZipAutoImportFromPath(false);
    setImportZipCodexHandoffContext(null);
    setImportZipImportContext(null);
  };

  const handleOpenDownloadZipWithDocs = () => {
    setDownloadZipDialogOpen(true);
  };

  const handleCloseDownloadZipWithDocs = () => {
    setDownloadZipDialogOpen(false);
  };

  const copyTextToClipboard = async (text) => {
    if (!text) {
      return false;
    }

    if (
      navigator &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    return false;
  };

  const buildExportAgentHandoffSummary = ({ includeDiffs = false, moduleName = selectedModule } = {}) => {
    const effectiveModule = moduleName && moduleName.trim().length > 0 ? moduleName : "ALL";
    const safeName = effectiveModule.toUpperCase() === "ALL" ? "all_modules" : effectiveModule.toLowerCase();
    const packageName = `${safeName}_dev_ide_files_with_docs.zip`;
    const changedPaths = (changeFiles || [])
      .map((file) => file.file_path || file.path)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    const changedCodePaths = changedPaths.filter((path) => !String(path).startsWith("docs/"));
    const changedDocumentPaths = changedPaths.filter((path) => String(path).startsWith("docs/"));
    const workflowLines = WORKFLOW_STATUS_STEPS.map(([key, label]) => {
      const status = workflowStatus?.[key] || {};
      const state = status.state || "pending";
      const message = status.message ? ` - ${status.message}` : "";
      return `${label}: ${state}${message}`;
    });

    return buildAgentHandoffSummary({
      phase: "Export",
      projectId: selectedProjectId || "",
      codeModule: effectiveModule,
      includeDocs: true,
      docsMode: "all",
      docModules: ["ALL"],
      packageName,
      changedCodePaths,
      changedDocumentPaths,
      verificationLines: workflowLines,
      warnings: includeDiffs
        ? ["Export handoff can list active change files, but source diffs are generated during import review where original and updated content are available."]
        : [],
      errors: [],
      recommendedNextStep: "Attach the exported ZIP and this handoff summary to the implementation agent, then import the returned package through DEV IDE.",
      codeDiffsText: includeDiffs ? "(Export-stage code diffs are not available from this panel. Use Import Review > Copy Summary with Diffs after re-import.)" : "",
      documentDiffsText: includeDiffs ? "(Export-stage document diffs are not available from this panel. Use Import Review > Copy Summary with Diffs after re-import.)" : "",
      includeDiffs,
    });
  };

  const handleCopyExportAgentHandoffSummary = async ({ includeDiffs = false } = {}) => {
    const moduleName = selectedModule && selectedModule.trim().length > 0 ? selectedModule : "ALL";
    const summary = buildExportAgentHandoffSummary({ includeDiffs, moduleName });
    try {
      const copied = await copyTextToClipboard(summary);
      if (!copied) {
        throw new Error("Clipboard copy was blocked by the browser.");
      }
      const message = includeDiffs
        ? "Agent export handoff summary with diff availability notes copied."
        : "Agent export handoff summary copied.";
      setActionMessage(message);
      markWorkflowStep("verificationSummaryCopied", "success", message);
      return true;
    } catch (err) {
      const message = err?.message || "Copy agent export handoff summary failed.";
      setActionMessage(message);
      markWorkflowStep("verificationSummaryCopied", "failure", message);
      return false;
    }
  };

  const handleAgentExportPackage = async () => {
    if (!activeChange || !activeChange.id) {
      const message = "Select an active change before exporting an agent package.";
      setActionMessage(message);
      markWorkflowStep("packageExported", "failure", message);
      return;
    }

    const moduleName =
      selectedModule && selectedModule.trim().length > 0 ? selectedModule : "ALL";

    const prompt = buildZipImplementationPrompt({
      projectId: selectedProjectId || "",
      codeModule: moduleName,
      includeDocs: true,
      docsMode: "all",
      docModules: ["ALL"],
      changeTitle: activeChange?.title || "",
      changeDescription: activeChange?.description || "",
      docsChanged: "Yes - update existing docs included in the ZIP when needed",
    });

    let copiedPrompt = false;
    try {
      copiedPrompt = await copyTextToClipboard(prompt);
    } catch (err) {
      copiedPrompt = false;
    }

    const exportHandoffSummary = buildExportAgentHandoffSummary({ moduleName });
    let copiedHandoff = false;
    if (copiedPrompt) {
      try {
        copiedHandoff = await copyTextToClipboard(`${prompt}\n\n${"=".repeat(80)}\n\n${exportHandoffSummary}`);
      } catch (err) {
        copiedHandoff = false;
      }
    }

    const successMessage = copiedHandoff
      ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt and agent handoff summary copied to clipboard.`
      : copiedPrompt
        ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt copied to clipboard.`
        : `Agent export package started for module '${moduleName}' with all documents. Clipboard copy was blocked by the browser.`;

    const downloaded = await handleDownloadModuleZip({
      codeModule: moduleName,
      includeDocs: true,
      docModules: ["ALL"],
      successMessage,
    });
    markWorkflowStep(
      "packageExported",
      downloaded ? "success" : "failure",
      downloaded ? successMessage : "Agent export package download failed.",
    );
  };

  const buildNewAppDefaults = () => {
    const projectBase = "new_app_sandbox";
    return {
      app_name: "New App",
      project_id: projectBase,
      base_path: `/opt/apps/${projectBase}`,
      backend_root_path: `/opt/apps/${projectBase}`,
      frontend_root_path: `/opt/apps/${projectBase}/frontend`,
      backend_port: 5050,
      starter_module: "home",
      server_name: "_",
      overwrite_existing: false,
      enable_systemd: true,
      enable_nginx: true,
      auto_build_frontend: true,
      open_firewall: true,
    };
  };

  const handleOpenNewApp = () => {
    setNewAppForm(buildNewAppDefaults());
    setNewAppPreview(null);
    setNewAppError(null);
    setNewAppOpen(true);
  };

  const handleCloseNewApp = () => {
    if (newAppPreviewLoading || newAppCreateLoading) return;
    setNewAppOpen(false);
  };

  const handleChangeNewAppField = (field, value) => {
    setNewAppForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "project_id") {
        const projectId = String(value || "").trim();
        if (projectId) {
          next.base_path = `/opt/apps/${projectId}`;
          next.backend_root_path = `/opt/apps/${projectId}`;
          next.frontend_root_path = `/opt/apps/${projectId}/frontend`;
        }
      }

      if (field === "base_path") {
        const basePath = String(value || "").trim();
        if (basePath) {
          next.backend_root_path = `${basePath}`;
          next.frontend_root_path = `${basePath}/frontend`;
        }
      }

      return next;
    });
  };

  const readResponsePayload = async (res) => {
    const contentType = String(res.headers.get("content-type") || "").toLowerCase();
    const rawText = await res.text();

    let data = null;
    if (rawText) {
      const looksJson =
        contentType.includes("application/json") || /^\s*[\[{]/.test(rawText);
      if (looksJson) {
        try {
          data = JSON.parse(rawText);
        } catch (err) {
          data = null;
        }
      }
    }

    return {
      ok: Boolean(res.ok),
      status: res.status,
      statusText: res.statusText || "",
      contentType,
      rawText,
      data,
    };
  };

  const buildResponseErrorMessage = (payloadResult, fallbackMessage) => {
    if (payloadResult?.data) {
      return (
        payloadResult.data.message ||
        payloadResult.data.error ||
        fallbackMessage
      );
    }

    const statusLabel = payloadResult
      ? `${payloadResult.status} ${payloadResult.statusText || ""}`.trim()
      : "";
    const compactText = String(payloadResult?.rawText || "")
      .replace(/\s+/g, " ")
      .trim();
    const shortText = compactText ? compactText.slice(0, 220) : "";

    return [fallbackMessage, statusLabel ? `(${statusLabel})` : "", shortText ? `: ${shortText}` : ""]
      .join(" ")
      .trim();
  };

  const buildExpectedNewAppUrl = (form) => {
    const host = "149.28.176.255";
    const port = Number(form?.backend_port || 0);
    if (!port) return "";
    if (form?.enable_nginx) {
      return `http://${host}:${port}/`;
    }
    return `http://${host}:${port}/api/health`;
  };

  const checkProjectExists = async (projectId) => {
    if (!projectId) return false;
    const res = await fetch("/api/dev-ide/projects");
    const payloadResult = await readResponsePayload(res);
    if (!payloadResult.ok || !payloadResult.data?.success) {
      return false;
    }
    const list = Array.isArray(payloadResult.data.projects)
      ? payloadResult.data.projects
      : [];
    return list.some((project) => String(project?.project_id || "") === String(projectId));
  };

  const handlePreviewNewApp = async () => {
    setNewAppPreviewLoading(true);
    setNewAppError(null);
    setNewAppPreview(null);

    try {
      const payload = {
        ...newAppForm,
        label: newAppForm.app_name,
        base_modules: [String(newAppForm.starter_module || "home").trim() || "home"],
        enable_systemd: Boolean(newAppForm.enable_systemd),
        enable_nginx: Boolean(newAppForm.enable_nginx),
        overwrite_existing: Boolean(newAppForm.overwrite_existing),
        auto_build_frontend: Boolean(newAppForm.auto_build_frontend),
        open_firewall: Boolean(newAppForm.open_firewall),
      };

      const res = await fetch("/api/dev-ide/apps/preview-create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const payloadResult = await readResponsePayload(res);
      if (!payloadResult.ok || !payloadResult.data?.success) {
        throw new Error(
          buildResponseErrorMessage(payloadResult, "Failed to preview app.")
        );
      }
      setNewAppPreview(payloadResult.data);
    } catch (err) {
      setNewAppError(err.message || String(err));
    } finally {
      setNewAppPreviewLoading(false);
    }
  };

  const handleCreateNewApp = async () => {
    setNewAppCreateLoading(true);
    setNewAppError(null);

    try {
      const payload = {
        ...newAppForm,
        label: newAppForm.app_name,
        base_modules: [String(newAppForm.starter_module || "home").trim() || "home"],
        enable_systemd: Boolean(newAppForm.enable_systemd),
        enable_nginx: Boolean(newAppForm.enable_nginx),
        overwrite_existing: Boolean(newAppForm.overwrite_existing),
        auto_build_frontend: Boolean(newAppForm.auto_build_frontend),
        open_firewall: Boolean(newAppForm.open_firewall),
      };

      const expectedAppUrl = buildExpectedNewAppUrl(payload);
      const res = await fetch("/api/dev-ide/apps/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const payloadResult = await readResponsePayload(res);

      let data = payloadResult.data;
      if ((!payloadResult.ok || !data?.success) && !data) {
        const looksHtml = /<!doctype|<html/i.test(payloadResult.rawText || "");
        if (payloadResult.ok && looksHtml) {
          const projectExists = await checkProjectExists(payload.project_id);
          if (projectExists) {
            data = {
              success: true,
              project_id: payload.project_id,
              app_url: expectedAppUrl,
              message: "App appears to have been created successfully.",
              warnings: [
                "Create response returned HTML instead of JSON, but the project now exists.",
              ],
            };
          }
        }
      }

      if (!payloadResult.ok || !data?.success) {
        throw new Error(
          buildResponseErrorMessage(payloadResult, "Failed to create app.")
        );
      }

      await loadProjects();
      if (data.project_id) {
        setSelectedProjectId(data.project_id);
      }
      const openUrl = data.app_url ? ` Open ${data.app_url}` : '';
      setActionMessage(`App '${data.project_id}' created.${openUrl}`);
      setNewAppOpen(false);
      setNewAppPreview(data);
    } catch (err) {
      setNewAppError(err.message || String(err));
    } finally {
      setNewAppCreateLoading(false);
    }
  };

  const handleOpenNewModule = () => {
    const defaultName =
      selectedModule && selectedModule !== "ALL" ? selectedModule : "";
    setNewModuleName(defaultName);
    setNewModuleParent(""); // <-- NEW
    setNewModuleBackend(true);
    setNewModuleFrontend(true);
    setNewModuleError(null);
    setNewModuleOpen(true);
  };

  const handleCloseNewModule = () => {
    setNewModuleOpen(false);
  };

  const handleCreateModule = async () => {
    if (!selectedProjectId) {
      setNewModuleError("Please select a project first.");
      return;
    }

    if (!newModuleName.trim()) {
      setNewModuleError("Module name is required.");
      return;
    }
    if (!newModuleBackend && !newModuleFrontend) {
      setNewModuleError("Select at least one of backend or frontend.");
      return;
    }

    setNewModuleLoading(true);
    setNewModuleError(null);

    try {
      const payload = {
        module_name: newModuleName.trim(),
        create_backend: newModuleBackend,
        create_frontend: newModuleFrontend,
        project_id: selectedProjectId || undefined,
      };

      if (newModuleParent && newModuleParent.trim()) {
        payload.parent_module = newModuleParent.trim(); // <-- NEW
      }

      const res = await fetch("/api/dev-ide/create-module", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(
          (data.errors && data.errors) ||
            data.error ||
            "Failed to create module."
        );
      }

      setActionMessage(
        `Module '${newModuleName.trim()}' created on disk. Run backend/frontend scans to index new files into the Dev IDE.`
      );
      setNewModuleOpen(false);

      await refreshFilesList();
    } catch (err) {
      setNewModuleError(err.message);
    } finally {
      setNewModuleLoading(false);
    }
  };

  const handleOpenNewFile = () => {
    const defaultModule =
      selectedModule && selectedModule !== "ALL"
        ? selectedModule
        : modules[0] || "";
    setNewFileModule(defaultModule);
    setNewFileSide("backend");
    setNewFileFolder("routes");
    setNewFileName("");
    setNewFileType("");
    setNewFileError(null);
    setNewFileOpen(true);
  };

  const handleCloseNewFile = () => {
    setNewFileOpen(false);
  };

  const handleCreateFile = async () => {
    if (!newFileModule.trim()) {
      setNewFileError("Module is required.");
      return;
    }
    if (!newFileName.trim()) {
      setNewFileError("File name is required.");
      return;
    }

    setNewFileLoading(true);
    setNewFileError(null);

    try {
      if (!selectedProjectId) {
        throw new Error("Please select a project first.");
      }

      const payload = {
        module: newFileModule.trim(),
        side: newFileSide,
        folder: newFileFolder,
        file_name: newFileName.trim(),
        project_id: selectedProjectId || undefined,
        change_id: activeChange && activeChange.id ? activeChange.id : undefined,
        username: currentUsername || "dev-ide",
      };
      if (newFileType.trim()) {
        payload.file_type = newFileType.trim();
      }

      const res = await fetch("/api/dev-ide/create-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(
          (data.errors && data.errors) ||
            data.error ||
            "Failed to create file."
        );
      }

      const createdFile =
        data.file || data.created_file || data.result || null;
      const createdRelativePath =
        (createdFile && createdFile.relative_path) ||
        (createdFile && createdFile.path) ||
        null;

      try {
        if (newFileSide === "backend") {
          await handleRunBackendScan();
        } else if (newFileSide === "frontend") {
          await handleRunFrontendScan();
        }
      } catch (scanErr) {
        setActionMessage(
          scanErr?.message ||
            "File created, but running the scan failed. You may need to run it manually."
        );
      }

      const updatedFiles = await refreshFilesList();

      if (createdRelativePath && Array.isArray(updatedFiles)) {
        const match = updatedFiles.find(
          (f) => f.path === createdRelativePath
        );
        if (match && match.id && typeof handleSelectFile === "function") {
          try {
            await handleSelectFile(match.id);
          } catch {
            // Best-effort; ignore if handler signature differs.
          }
        }
      }

      setActionMessage(
        `File '${newFileName.trim()}' created in module '${newFileModule.trim()}'. ` +
          `Scan completed and the new file has been opened in the editor.`
      );
      setNewFileOpen(false);
    } catch (err) {
      setNewFileError(err.message);
    } finally {
      setNewFileLoading(false);
    }
  };

  const guessNextSqlSequence = () => {
    const files = sqlFiles || [];
    if (files.length === 0) {
      return "001";
    }

    const maxSequence = files.reduce((max, file) => {
      const path = file?.path || file?.filename || "";
      const match = path.match(/CHG-\d+-(\d+)\.sql$/i);
      if (!match) {
        return max;
      }
      const sequence = parseInt(match[1], 10);
      return Number.isNaN(sequence) ? max : Math.max(max, sequence);
    }, 0);

    return String(maxSequence + 1).padStart(3, "0");
  };

  const handleOpenNewSql = () => {
    if (!activeChange || !activeChange.id) {
      setActionMessage(
        "Set an active change before creating an SQL migration file."
      );
      return;
    }
    setNewSqlSequence(guessNextSqlSequence());
    setNewSqlDescription("");
    setNewSqlError(null);
    setNewSqlOpen(true);
  };

  const handleCloseNewSql = () => {
    setNewSqlOpen(false);
  };

  const handleCreateSqlFile = async () => {
    if (!activeChange || !activeChange.id) {
      setNewSqlError("No active change selected.");
      return;
    }
    if (!newSqlSequence.trim()) {
      setNewSqlError("Sequence is required (e.g. 001).");
      return;
    }

    setNewSqlLoading(true);
    setNewSqlError(null);

    try {
      const payload = {
        change_id: activeChange.id,
        sequence: newSqlSequence.trim(),
        description: newSqlDescription.trim() || null,
        project_id: selectedProjectId || undefined,
      };

      const res = await fetch("/api/dev-ide/sql/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to create SQL file.");
      }

      const provenanceWarning = data?.provenance?.status === "RECOVERY_REQUIRED"
        ? " Permanent provenance requires recovery; the SQL file itself was created successfully."
        : "";
      setActionMessage(
        `SQL migration created for change #${
          activeChange.id
        } with sequence ${newSqlSequence.trim().padStart(3, "0")}.${provenanceWarning}`
      );
      setNewSqlOpen(false);

      await loadSqlFiles(activeChange.id);
    } catch (err) {
      setNewSqlError(err.message);
    } finally {
      setNewSqlLoading(false);
    }
  };

  const handleOpenSqlEditorForFile = async (file) => {
    if (!file) return;

    setSelectedSqlFile(file);
    setSqlEditorOpen(true);
    setSqlEditorLoading(true);
    setSqlEditorError(null);
    setSqlSaveMessage(null);

    try {
      const fileId = file.file_id || file.id;
      const res = await fetch(
        `/api/dev-ide/sql/content?file_id=${encodeURIComponent(fileId)}${
          selectedProjectId
            ? `&project_id=${encodeURIComponent(selectedProjectId)}`
            : ""
        }`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load SQL file.");
      }
      const sqlFile = data.file || {};
      const content = sqlFile.content || "";
      setSqlEditorContent(content);
      setSqlEditorOriginalContent(content);
      setSelectedSqlFile((prev) => ({ ...(prev || {}), ...sqlFile }));
    } catch (err) {
      setSqlEditorError(err.message);
    } finally {
      setSqlEditorLoading(false);
    }
  };

  const handleCloseSqlEditorDialog = () => {
    setSqlEditorOpen(false);
  };

  const handleSaveSqlEditor = async () => {
    if (!selectedSqlFile) return;

    setSqlSaveLoading(true);
    setSqlEditorError(null);
    setSqlSaveMessage(null);

    try {
      const fileId = selectedSqlFile.file_id || selectedSqlFile.id;
      const res = await fetch("/api/dev-ide/sql/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_id: fileId,
          content: sqlEditorContent,
          username: currentUsername || "unknown",
          project_id: selectedProjectId || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save SQL file.");
      }

      setSqlEditorOriginalContent(sqlEditorContent);
      setSqlSaveMessage(
        data?.provenance?.status === "RECOVERY_REQUIRED"
          ? "SQL file saved successfully. Permanent provenance requires recovery."
          : "SQL file saved successfully."
      );

      if (activeChange && activeChange.id) {
        await loadSqlFiles(activeChange.id);
      }
    } catch (err) {
      setSqlEditorError(err.message);
    } finally {
      setSqlSaveLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // HANDLERS (ZIP DOWNLOAD)
  // ---------------------------------------------------------------------------

  const handleDownloadModuleZip = async (options = {}) => {
    const moduleName =
      options.codeModule ||
      (selectedModule && selectedModule.trim().length > 0 ? selectedModule : "ALL");

    const params = new URLSearchParams();
    params.set("module", moduleName.toUpperCase() === "ALL" ? "ALL" : moduleName);
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }
    if (options.linkedChangeId) {
      params.set("linked_change_id", String(options.linkedChangeId));
    }
    if (options.includeDocs) {
      params.set("include_documents", "true");
      const docModules = Array.isArray(options.docModules) ? options.docModules : [];
      if (docModules.length && !docModules.includes("ALL")) {
        params.set("docs_modules", docModules.join(","));
      }
    }

    try {
      const res = await fetch(`/api/dev-ide/zip-module?${params.toString()}`, { method: "GET" });

      if (!res.ok) {
        let errMsg = `Failed to download module ZIP (status ${res.status}).`;
        try {
          const data = await res.json();
          if (data && data.error) {
            errMsg = data.error;
          }
        } catch {}
        setActionMessage(errMsg);
        return false;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = moduleName.toUpperCase() === "ALL" ? "all_modules" : moduleName.toLowerCase();
      const suffix = options.includeDocs ? "_with_docs" : "";

      link.href = url;
      link.download = `${safeName}_dev_ide_files${suffix}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setActionMessage(
        options.successMessage ||
          `Download started for module '${moduleName}'${options.includeDocs ? " with documents" : ""}.`,
      );
      return true;
    } catch (err) {
      setActionMessage(err?.message || "Unexpected error while downloading module ZIP.");
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // HANDLERS (EXTRACTED GROUPS)
  // ---------------------------------------------------------------------------

  const {
    handleSelectFile,
    handleCopyFullForGPT,
    handleCopySelectionForGPT,
    handleApplyGptPaste,
    handlePreviewDiff,
    handleSaveFile,
  } = createFileEditorHandlers({
    currentUsername,
    selectedFileId,
    setSelectedFileId,
    selectedFileMeta,
    setSelectedFileMeta,
    fileContent,
    setFileContent,
    fileOriginalContent,
    setFileOriginalContent,
    fileLoading,
    setFileLoading,
    fileError,
    setFileError,
    gptPasteContent,
    setGptPasteContent,
    changeSummary,
    setChangeSummary,
    saveLoading,
    setSaveLoading,
    saveMessage,
    setSaveMessage,
    diffText,
    setDiffText,
    diffLoading,
    setDiffLoading,
    diffError,
    setDiffError,
    diffLineCount,
    setDiffLineCount,
    fileHistory,
    setFileHistory,
    fileHistoryError,
    setFileHistoryError,
    activeChange,
    loadChangeFiles,
    loadSqlFiles,
    // NEW: pass popup handlers to file editor handlers
    setSaveErrorMessage,
    setSaveErrorOpen,
    // NEW: project-aware file operations
    selectedProjectId,
  });

  const {
    handleRestartSandboxBackend,
    handleRebuildSandboxFrontend,
    handleRunBackendScan,
    handleRunFrontendScan,
    handleDescribeFile,
  } = createSandboxHandlers({
    setBackendActionLoading,
    setFrontendActionLoading,
    setActionMessage,
    selectedFileMeta,
    // NEW: pass popup handlers to sandbox handlers for rebuild errors
    setSaveErrorMessage,
    setSaveErrorOpen,
    // NEW: project-aware (optional)
    selectedProjectId,
  });

  // ---------------------------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------------------------

  const handleImportedSaved = async () => {
    try {
      await refreshFilesList();
      if (activeChange && activeChange.id) {
        await loadChangeFiles(activeChange.id);
        await loadSqlFiles(activeChange.id);
      }
    } catch {
      // non-critical
    }
  };

  return {
    // NEW: projects selector
    projects,
    projectsLoading,
    projectsError,
    selectedProjectId,
    setSelectedProjectId,

    // core state
    files,
    filesLoading,
    filesError,
    modules,
    selectedModule,
    setSelectedModule,
    selectedFileId,
    selectedFileMeta,
    fileContent,
    setFileContent,
    fileOriginalContent,
    fileLoading,
    fileError,
    gptPasteContent,
    setGptPasteContent,
    changeSummary,
    setChangeSummary,
    saveLoading,
    saveMessage,
    saveErrorMessage,
    saveErrorOpen,
    setSaveErrorOpen,
    filterText,
    setFilterText,
    diffText,
    setDiffText,
    diffLoading,
    diffError,
    diffLineCount,
    backendActionLoading,
    frontendActionLoading,
    actionMessage,
    workflowStatus,
    workflowStatusLoading,
    workflowStatusError,
    markWorkflowStep,
    resetWorkflowStatus,
    changes,
    activeChange,
    changeFiles,
    changeFilterMode,
    setChangeFilterMode,
    changeError,
    revertWholeChangeLoading,
    fileHistory,
    fileHistoryLoading,
    fileHistoryError,

    // rules panel (CHG-037)
    rulesMarkdown,
    rulesLoading,
    rulesError,

    // module + SQL
    sqlFiles,
    sqlLoading,
    sqlError,
    handleDownloadModuleZip,
    handleOpenDownloadZipWithDocs,
    handleCloseDownloadZipWithDocs,
    handleAgentExportPackage,
    handleCopyExportAgentHandoffSummary,

    // dialogs
    newAppOpen,
    newAppForm,
    newAppPreview,
    newAppError,
    newAppPreviewLoading,
    newAppCreateLoading,
    newModuleOpen,
    newModuleName,
    setNewModuleName,
    newModuleParent, // <-- NEW
    setNewModuleParent, // <-- NEW
    newModuleBackend,
    setNewModuleBackend,
    newModuleFrontend,
    setNewModuleFrontend,
    newModuleError,
    newModuleLoading,

    // ZIP import dialog
    importZipOpen,
    importZipIncludeDocs,
    importZipInitialPath,
    importZipAutoImportFromPath,
    importZipCodexHandoffContext,
    importZipImportContext,
    downloadZipDialogOpen,

    newFileOpen,
    newFileModule,
    setNewFileModule,
    newFileSide,
    setNewFileSide,
    newFileFolder,
    setNewFileFolder,
    newFileName,
    setNewFileName,
    newFileType,
    setNewFileType,
    newFileError,
    newFileLoading,

    newSqlOpen,
    newSqlSequence,
    setNewSqlSequence,
    newSqlDescription,
    setNewSqlDescription,
    newSqlError,
    newSqlLoading,

    sqlEditorOpen,
    selectedSqlFile,
    sqlEditorContent,
    setSqlEditorContent,
    sqlEditorOriginalContent,
    sqlEditorLoading,
    sqlEditorError,
    sqlSaveLoading,
    sqlSaveMessage,

    // derived
    filteredFiles,

    // handlers (core)
    handleSelectActiveChange,
    handleCreateNewChange,
    handleRevertWholeChange,

    // handlers (editor)
    handleSelectFile,
    handleCopyFullForGPT,
    handleCopySelectionForGPT,
    handleApplyGptPaste,
    handlePreviewDiff,
    handleSaveFile,

    // handlers (sandbox)
    handleRestartSandboxBackend,
    handleRebuildSandboxFrontend,
    handleRunBackendScan,
    handleRunFrontendScan,
    handleDescribeFile,

    // handlers (history + dialogs + SQL)
    handleLoadFileHistory,
    handleRevertFile,
    handleOpenImportZip,
    handleOpenImportZipWithDocs,
    handleCloseImportZip,
    handleImportedSaved,
    handleOpenNewApp,
    handleCloseNewApp,
    handleChangeNewAppField,
    handlePreviewNewApp,
    handleCreateNewApp,
    handleOpenNewModule,
    handleCloseNewModule,
    handleCreateModule,
    handleOpenNewFile,
    handleCloseNewFile,
    handleCreateFile,
    handleOpenNewSql,
    handleCloseNewSql,
    handleCreateSqlFile,
    handleOpenSqlEditorForFile,
    handleCloseSqlEditorDialog,
    handleSaveSqlEditor,
  };
};

export default useDevIdePanelState;
